import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import VideoCard from './VideoCard';
import { videoAPI, messageAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { prefetchCommonRoutes } from "../routePrefetch";
import VideoNavigation from "./VideoNavigation";

const PAGE_SIZE = 10;
const WINDOW_RADIUS = 1;

function extractVideos(data) {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.videos)) {
    return data.videos;
  }

  return [];
}

function VideoFeed() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [showGuestModal, setShowGuestModal] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextPage, setNextPage] = useState(2);
  const [activeIndex, setActiveIndex] = useState(0);
  const [inboxBadge, setInboxBadge] = useState(0);

  const touchStartY = useRef(null);
  const touchStartX = useRef(null);
  const touchStartTime = useRef(null);
  const isAnimating = useRef(false);
  const outerRef = useRef(null);
  const loadedPages = useRef(new Set());

  const loadPage = useCallback(async (page, replace = false) => {
    if (loadedPages.current.has(page)) {
      return false;
    }

    loadedPages.current.add(page);

    try {
      const response = await videoAPI.getFeed(
        page,
        PAGE_SIZE,
        {
          cache: page === 1,
        }
      );

      const data = response.data;

      const incoming = extractVideos(data).filter(
        (video) =>
          video &&
          video.id &&
          video.video_file
      );

      setVideos((previous) => {
        if (replace) {
          return incoming;
        }

        const existingIds = new Set(
          previous.map((video) => video.id)
        );

        const newVideos = incoming.filter(
          (video) => !existingIds.has(video.id)
        );

        return [...previous, ...newVideos];
      });

      const next =
        data && !Array.isArray(data)
          ? data.next
          : null;

      setHasMore(
        Boolean(next) ||
        incoming.length === PAGE_SIZE
      );

      setNextPage(page + 1);

      return incoming.length > 0;
    } catch (error) {
      loadedPages.current.delete(page);

      console.error(
        `Feed page ${page} error:`,
        error
      );

      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initialLoad = async () => {
      setLoading(true);

      const loaded = await loadPage(1, true);

      if (!cancelled) {
        setLoading(false);

        if (!loaded) {
          setHasMore(false);
        }
      }
    };

    initialLoad();

    return () => {
      cancelled = true;
    };
  }, [loadPage]);

  /*
   * Prefetch common destinations after the feed has
   * become idle. This keeps route navigation fast
   * without competing with initial video loading.
   */
  useEffect(() => {
    let idleId = null;
    let timeoutId = null;

    const runPrefetch = () => {
      prefetchCommonRoutes();
    };

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(runPrefetch, {
        timeout: 5000,
      });
    } else {
      timeoutId = window.setTimeout(runPrefetch, 2500);
    }

    return () => {
      if (idleId !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  /*
   * One inbox poll for the entire feed.
   * Previously each active VideoCard handled this itself.
   */
  useEffect(() => {
    if (!user) {
      setInboxBadge(0);
      return undefined;
    }

    let cancelled = false;

    const fetchInbox = async () => {
      try {
        const response =
          await messageAPI.getConversations();

        const conversations =
          Array.isArray(response.data)
            ? response.data
            : [];

        const totalUnread =
          conversations.reduce(
            (sum, conversation) =>
              sum +
              (
                Number.parseInt(
                  conversation.unread,
                  10
                ) || 0
              ),
            0
          );

        if (!cancelled) {
          setInboxBadge(totalUnread);
        }
      } catch (_) {
        /*
         * Inbox failure should never stop
         * the video feed.
         */
      }
    };

    fetchInbox();

    const interval = setInterval(
      fetchInbox,
      30000
    );

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  const maybeLoadMore = useCallback(
    (index) => {
      if (!hasMore || loadingMore) {
        return;
      }

      if (index < videos.length - 3) {
        return;
      }

      setLoadingMore(true);

      loadPage(nextPage)
        .finally(() => {
          setLoadingMore(false);
        });
    },
    [
      hasMore,
      loadingMore,
      loadPage,
      nextPage,
      videos.length,
    ]
  );

  const goTo = useCallback(
    (index) => {
      if (
        isAnimating.current ||
        index < 0 ||
        index >= videos.length
      ) {
        return;
      }

      maybeLoadMore(index);

      isAnimating.current = true;

      setActiveIndex(index);

      window.setTimeout(() => {
        isAnimating.current = false;
      }, 450);
    },
    [
      maybeLoadMore,
      videos.length,
    ]
  );

  const handleTouchStart = (event) => {
    if (!event.touches?.length) {
      return;
    }

    touchStartY.current =
      event.touches[0].clientY;

    touchStartX.current =
      event.touches[0].clientX;

    touchStartTime.current =
      Date.now();
  };

  const handleTouchEnd = (event) => {
    if (
      touchStartY.current === null ||
      !event.changedTouches?.length
    ) {
      return;
    }

    const currentTouch =
      event.changedTouches[0];

    const deltaY =
      touchStartY.current -
      currentTouch.clientY;

    const deltaX =
      Math.abs(
        touchStartX.current -
        currentTouch.clientX
      );

    const elapsed =
      Date.now() -
      touchStartTime.current;

    if (
      Math.abs(deltaY) > 25 &&
      Math.abs(deltaY) > deltaX &&
      elapsed < 700
    ) {
      goTo(
        deltaY > 0
          ? activeIndex + 1
          : activeIndex - 1
      );
    }

    touchStartY.current = null;
    touchStartX.current = null;
    touchStartTime.current = null;
  };

  const handleWheel = useCallback(
    (event) => {
      event.preventDefault();

      if (isAnimating.current) {
        return;
      }

      if (event.deltaY > 20) {
        goTo(activeIndex + 1);
      } else if (event.deltaY < -20) {
        goTo(activeIndex - 1);
      }
    },
    [activeIndex, goTo]
  );

  useEffect(() => {
    const element = outerRef.current;

    if (!element) {
      return undefined;
    }

    element.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      }
    );

    return () => {
      element.removeEventListener(
        'wheel',
        handleWheel
      );
    };
  }, [handleWheel]);

  if (loading) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: '3px solid #333',
            borderTopColor: '#fff',
            animation:
              'spin 0.8s linear infinite',
          }}
        />

        <style>
          {`
            @keyframes spin {
              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#000',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            fontSize: 56,
          }}
        >
          🎬
        </div>

        <p
          style={{
            color: '#fff',
            fontSize: 20,
            fontWeight: 700,
          }}
        >
          No videos yet
        </p>

        <a
          href="/upload"
          style={{
            marginTop: 8,
            padding: '12px 28px',
            borderRadius: 999,
            background: '#fff',
            color: '#000',
            fontWeight: 700,
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          Upload First Video
        </a>
      </div>
    );
  }

  /*
   * Only mount the current video and one
   * adjacent video on either side.
   */
  const start = Math.max(
    0,
    activeIndex - WINDOW_RADIUS
  );

  const end = Math.min(
    videos.length - 1,
    activeIndex + WINDOW_RADIUS
  );

  const visibleVideos = [];

  for (
    let index = start;
    index <= end;
    index += 1
  ) {
    visibleVideos.push({
      video: videos[index],
      index,
    });
  }

  return (
    <div
      ref={outerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#000',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 480,
          height: '100%',
          overflow: 'hidden',
          background: '#000',
        }}
      >
        {visibleVideos.map(
          ({ video, index }) => (
            <div
              key={video.id}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                transform:
                  `translateY(${
                    (index - activeIndex) * 100
                  }%)`,
                transition:
                  'transform 0.45s cubic-bezier(0.25,0.46,0.45,0.94)',
                willChange: 'transform',
                overflow: 'hidden',
              }}
            >
              <VideoCard
                video={video}
                isActive={
                  index === activeIndex
                }
                isNearActive={
                  Math.abs(
                    index - activeIndex
                  ) <= 1
                }
                requireAuth={() => {
                  if (!user) {
                    setShowGuestModal(true);
                    return true;
                  }

                  return false;
                }}
              />
            </div>
          )
        )}
      </div>

      <VideoNavigation inboxBadge={inboxBadge} />

      {loadingMore && (
        <div
          style={{
            position: 'fixed',
            bottom: 12,
            left: '50%',
            transform:
              'translateX(-50%)',
            zIndex: 999,
            color: '#fff',
            fontSize: 11,
            background:
              'rgba(0,0,0,.55)',
            padding: '6px 10px',
            borderRadius: 20,
          }}
        >
          Loading…
        </div>
      )}

      {showGuestModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background:
              'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() =>
            setShowGuestModal(false)
          }
        >
          <div
            style={{
              background: '#1a1a1a',
              borderRadius: 20,
              padding: '32px 24px',
              width: '85%',
              maxWidth: 360,
              textAlign: 'center',
            }}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div
              style={{
                fontSize: 48,
                marginBottom: 12,
              }}
            >
              🎂
            </div>

            <h2
              style={{
                color: '#fff',
                fontSize: 24,
                fontWeight: 900,
                fontFamily:
                  'DM Sans,sans-serif',
                marginBottom: 6,
              }}
            >
              IcingShow
            </h2>

            <p
              style={{
                color:
                  'rgba(255,255,255,0.5)',
                fontSize: 14,
                fontFamily:
                  'DM Sans,sans-serif',
                marginBottom: 24,
              }}
            >
              Sign in to like, comment,
              share and earn IcingPoints
            </p>

            <button
              onClick={() => {
                setShowGuestModal(false);
                navigate('/login');
              }}
              style={{
                width: '100%',
                padding: '13px 0',
                borderRadius: 12,
                border: 'none',
                background:
                  'linear-gradient(135deg,#8B5FBF,#6a3fa0)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
                cursor: 'pointer',
                fontFamily:
                  'DM Sans,sans-serif',
                marginBottom: 10,
              }}
            >
              Log In
            </button>

            <button
              onClick={() => {
                setShowGuestModal(false);
                navigate('/register');
              }}
              style={{
                width: '100%',
                padding: '13px 0',
                borderRadius: 12,
                border: 'none',
                background:
                  'rgba(255,255,255,0.08)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
                cursor: 'pointer',
                fontFamily:
                  'DM Sans,sans-serif',
                marginBottom: 10,
              }}
            >
              Sign Up
            </button>

            <button
              onClick={() =>
                setShowGuestModal(false)
              }
              style={{
                background: 'none',
                border: 'none',
                color:
                  'rgba(255,255,255,0.3)',
                fontSize: 13,
                cursor: 'pointer',
                fontFamily:
                  'DM Sans,sans-serif',
              }}
            >
              Continue watching
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoFeed;